import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { toast } from 'sonner';
import api, { errMsg } from '../../../lib/api';
import { DAY_LABEL, Lbl, SEL } from './SessionDialog';
import { Pencil, Plus, Trash2 } from 'lucide-react';

const EMPTY = { name: '', role: 'BT', email: '', phone: '', max_hours_week: 40, availability: {}, active: true, notes: '' };

const StaffDialog = ({ open, initial, meta, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  const form = f || { ...EMPTY, ...(initial || {}) };
  const set = (k, v) => setF({ ...form, [k]: v });
  const slot = (d) => (form.availability?.[d] || [])[0];
  const setSlot = (d, k, v) => {
    const cur = slot(d) || { start: '08:00', end: '17:00' };
    set('availability', { ...form.availability, [d]: [{ ...cur, [k]: v }, ...(form.availability?.[d] || []).slice(1)] });
  };
  const toggleDay = (d) => {
    const av = { ...form.availability };
    if (slot(d)) delete av[d]; else av[d] = [{ start: '08:00', end: '17:00' }];
    set('availability', av);
  };
  const save = async () => {
    try {
      const body = { ...form, max_hours_week: Number(form.max_hours_week) || 0 };
      if (initial?.staff_id) await api.put(`/ludo/schedule/staff/${initial.staff_id}`, body);
      else await api.post('/ludo/schedule/staff', body);
      toast.success('Staff saved'); setF(null); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setF(null); onClose(); } }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sched-staff-dialog">
        <DialogHeader><DialogTitle className="font-serif">{initial?.staff_id ? 'Edit staff' : 'Add staff'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Lbl t="Name"><Input value={form.name} onChange={(e) => set('name', e.target.value)} className="rounded-xl h-9" data-testid="sched-staff-name" /></Lbl>
          <Lbl t="Role"><select className={SEL} value={form.role} onChange={(e) => set('role', e.target.value)} data-testid="sched-staff-role">
            {meta.roles.map((r) => <option key={r}>{r}</option>)}</select></Lbl>
          <Lbl t="Email"><Input value={form.email} onChange={(e) => set('email', e.target.value)} className="rounded-xl h-9" data-testid="sched-staff-email" /></Lbl>
          <Lbl t="Phone"><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} className="rounded-xl h-9" data-testid="sched-staff-phone" /></Lbl>
          <Lbl t="Max hrs / week"><Input type="number" value={form.max_hours_week} onChange={(e) => set('max_hours_week', e.target.value)} className="rounded-xl h-9" data-testid="sched-staff-max" /></Lbl>
          <Lbl t="Status"><select className={SEL} value={form.active ? 'yes' : 'no'} onChange={(e) => set('active', e.target.value === 'yes')} data-testid="sched-staff-active">
            <option value="yes">Active</option><option value="no">Inactive</option></select></Lbl>
        </div>
        <div className="text-[11px] uppercase tracking-wider text-[#6E5F78] font-semibold mt-2">Weekly availability</div>
        <div className="space-y-1.5">
          {meta.days.map((d) => (
            <div key={d} className="flex items-center gap-2" data-testid={`sched-avail-${d}`}>
              <button type="button" onClick={() => toggleDay(d)} data-testid={`sched-avail-toggle-${d}`}
                className={`w-14 text-[12px] py-1 rounded-full border ${slot(d) ? 'bg-[#5E5068] text-white border-[#5E5068]' : 'bg-white text-[#8A7B95] border-[#E5E0EA]'}`}>{DAY_LABEL[d]}</button>
              {slot(d) ? (<>
                <Input type="time" value={slot(d).start} onChange={(e) => setSlot(d, 'start', e.target.value)} className="h-8 w-28 rounded-lg" data-testid={`sched-avail-start-${d}`} />
                <span className="text-[#8A7B95]">–</span>
                <Input type="time" value={slot(d).end} onChange={(e) => setSlot(d, 'end', e.target.value)} className="h-8 w-28 rounded-lg" data-testid={`sched-avail-end-${d}`} />
              </>) : <span className="text-[12px] text-[#8A7B95]">Not available</span>}
            </div>
          ))}
        </div>
        <Button onClick={save} disabled={!form.name.trim()} className="rounded-xl bg-[#5E5068] text-white" data-testid="sched-staff-save">Save staff</Button>
      </DialogContent>
    </Dialog>
  );
};

const TimeOffForm = ({ staff, meta, onSaved }) => {
  const [f, setF] = useState({ staff_id: '', start_date: '', end_date: '', kind: 'PTO', note: '' });
  const save = async () => {
    try { await api.post('/ludo/schedule/time-off', { ...f, end_date: f.end_date || f.start_date }); toast.success('Time off saved — affected sessions now show as gaps'); setF({ ...f, start_date: '', end_date: '', note: '' }); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid sm:grid-cols-6 gap-2 items-end" data-testid="sched-timeoff-form">
      <Lbl t="Staff"><select className={SEL} value={f.staff_id} onChange={(e) => setF({ ...f, staff_id: e.target.value })} data-testid="sched-timeoff-staff">
        <option value="">Pick…</option>{staff.map((s) => <option key={s.staff_id} value={s.staff_id}>{s.name}</option>)}</select></Lbl>
      <Lbl t="Type"><select className={SEL} value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="sched-timeoff-kind">
        {meta.out_kinds.map((k) => <option key={k}>{k}</option>)}</select></Lbl>
      <Lbl t="From"><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="h-9 rounded-xl" data-testid="sched-timeoff-from" /></Lbl>
      <Lbl t="To"><Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className="h-9 rounded-xl" data-testid="sched-timeoff-to" /></Lbl>
      <Lbl t="Note"><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="h-9 rounded-xl" data-testid="sched-timeoff-note" /></Lbl>
      <Button onClick={save} disabled={!f.staff_id || !f.start_date} className="rounded-xl bg-[#5E5068] text-white h-9" data-testid="sched-timeoff-save">Add time off</Button>
    </div>
  );
};

export const StaffManager = ({ staff, timeOff, meta, reload }) => {
  const [edit, setEdit] = useState(null);
  const name = (id) => staff.find((s) => s.staff_id === id)?.name || '—';
  const archive = async (s) => {
    if (!window.confirm(`Archive ${s.name}? Their sessions become unassigned gaps.`)) return;
    try { await api.delete(`/ludo/schedule/staff/${s.staff_id}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const removeOut = async (o) => { try { await api.delete(`/ludo/schedule/time-off/${o.out_id}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-6" data-testid="sched-staff-manager">
      <div className="flex justify-between items-center">
        <h3 className="font-serif text-xl text-[#2D2833]">BTs & BCBAs</h3>
        <Button onClick={() => setEdit({})} className="rounded-full bg-[#5E5068] text-white" data-testid="sched-staff-add"><Plus size={14} className="mr-1" />Add staff</Button>
      </div>
      <div className="rounded-2xl border border-[#E5E0EA] bg-white divide-y divide-[#EEEAF2]">
        {staff.map((s) => (
          <div key={s.staff_id} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`sched-staff-${s.staff_id}`}>
            <div><b>{s.name}</b> <span className="text-[11px] text-[#8A7B95]">{s.role}{s.active === false ? ' · inactive' : ''} · max {s.max_hours_week} hrs · {Object.keys(s.availability || {}).map((d) => DAY_LABEL[d]).join(' ') || 'no availability set'}</span></div>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => setEdit(s)} data-testid={`sched-staff-edit-${s.staff_id}`}><Pencil size={13} /></Button>
              <Button variant="ghost" size="sm" onClick={() => archive(s)} className="text-[#8A3A3A]" data-testid={`sched-staff-archive-${s.staff_id}`}><Trash2 size={13} /></Button>
            </div>
          </div>
        ))}
        {staff.length === 0 && <div className="p-6 text-center text-[#8A7B95]" data-testid="sched-staff-empty">No staff yet. Add your BTs and BCBAs with their weekly availability.</div>}
      </div>
      <h3 className="font-serif text-xl text-[#2D2833]">PTO & call-outs</h3>
      <TimeOffForm staff={staff} meta={meta} onSaved={reload} />
      <div className="rounded-2xl border border-[#E5E0EA] bg-white divide-y divide-[#EEEAF2]">
        {timeOff.map((o) => (
          <div key={o.out_id} className="flex items-center justify-between px-4 py-2 text-sm" data-testid={`sched-timeoff-${o.out_id}`}>
            <span><b>{name(o.staff_id)}</b> · {o.kind} · {o.start_date}{o.end_date !== o.start_date ? ` → ${o.end_date}` : ''}{o.note ? ` · ${o.note}` : ''}</span>
            <Button variant="ghost" size="sm" onClick={() => removeOut(o)} data-testid={`sched-timeoff-remove-${o.out_id}`}><Trash2 size={13} /></Button>
          </div>
        ))}
        {timeOff.length === 0 && <div className="p-4 text-center text-[#8A7B95] text-sm">No time off logged.</div>}
      </div>
      <StaffDialog open={!!edit} initial={edit} meta={meta} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />
    </div>
  );
};

export default StaffManager;
